import { toast } from 'react-toastify';
import type { AppDispatch } from '@/store';
import { apiSlice } from '@/store/api/apiSlice';
import { API_CACHE_TAGS } from '@/store/api/apiTags';
import { getCurrentUser, logout, renewSessionTokens } from '@/store/slices/authSlice';
import { authService } from '@/services/authService';
import { storage } from '@/utils/storage';
import { refreshAccessToken } from '@/utils/tokenRefresh';

export type SessionLogoutReason = 'manual' | 'idle' | 'expired' | 'revoked' | 'startup';

export const LOGOUT_REASON_KEY = 'cotaskr_logout_reason';
export const SESSION_TOAST = {
  warn: 'session-jwt-warn',
  idleWarn: 'session-idle-warn',
  expired: 'session-expired',
} as const;

const LOGOUT_MESSAGES: Partial<Record<SessionLogoutReason, string>> = {
  idle: 'You were signed out after a period of inactivity.',
  expired: 'Your session has expired. Please sign in again.',
  revoked: 'Your session was ended. Please sign in again.',
  startup: 'Your previous session expired. Please sign in again.',
};

let logoutInProgress = false;

export function consumeLogoutReason(): SessionLogoutReason | null {
  try {
    const reason = sessionStorage.getItem(LOGOUT_REASON_KEY) as SessionLogoutReason | null;
    if (reason) sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return reason;
  } catch {
    return null;
  }
}

export function stashLogoutReason(reason: SessionLogoutReason) {
  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch {
    /* ignore */
  }
}

export function notifySessionLogout(reason: SessionLogoutReason) {
  stashLogoutReason(reason);
  window.dispatchEvent(new CustomEvent('cotaskr:session-logout', { detail: { reason } }));
}

function refetchSessionData(dispatch: AppDispatch) {
  dispatch(apiSlice.util.invalidateTags([...API_CACHE_TAGS]));
  void dispatch(getCurrentUser());
}

export async function tryRefreshSession(
  dispatch: AppDispatch,
  options?: { silent?: boolean },
): Promise<boolean> {
  const tokens = await refreshAccessToken();
  if (!tokens) return false;
  dispatch(renewSessionTokens(tokens));
  refetchSessionData(dispatch);
  toast.dismiss(SESSION_TOAST.warn);
  toast.dismiss(SESSION_TOAST.idleWarn);
  if (!options?.silent) {
    toast.success('Session extended. You are still signed in.', { autoClose: 3000 });
  }
  return true;
}

export async function performSessionLogout(
  dispatch: AppDispatch,
  reason: SessionLogoutReason,
  options?: {
    showToast?: boolean;
    navigate?: boolean;
    invalidateServer?: boolean;
  },
) {
  if (logoutInProgress) return;
  logoutInProgress = true;

  toast.dismiss(SESSION_TOAST.warn);
  toast.dismiss(SESSION_TOAST.idleWarn);

  const willNavigate = options?.navigate !== false;
  const showToast = options?.showToast ?? (!willNavigate && reason !== 'manual');
  if (showToast) {
    const message = LOGOUT_MESSAGES[reason];
    if (message) {
      toast.info(message, { toastId: SESSION_TOAST.expired, autoClose: 6000 });
    }
  }

  if (options?.invalidateServer !== false && reason === 'manual' && storage.getAccessToken()) {
    try {
      await authService.logout();
    } catch {
      /* local logout still proceeds */
    }
  }

  stashLogoutReason(reason);
  dispatch(logout());

  if (options?.navigate !== false) {
    const suffix = reason === 'manual' ? '' : `?reason=${reason}`;
    window.location.assign(`/login${suffix}`);
  }

  logoutInProgress = false;
}
